import React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import ImportBatchDialog from '../../../components/lab/ImportBatchDialog';
import { mockOrders } from './mockOrders';

const navItems = [
  { key: 'dashboard', label: 'Dashboard' },
  { key: 'orders', label: 'Orders' },
  { key: 'result-batchs', label: 'Result Batchs' },
];

const mockResultBatches = [
  { batchId: 'BATCH-7001', completionDate: '2026-02-25', active: true, breakdown: '35 normal, 6 pending' },
  { batchId: 'BATCH-7002', completionDate: '2026-02-24', active: false, breakdown: '29 normal, 4 pending, 2 flagged' },
  { batchId: 'BATCH-7003', completionDate: '2026-02-23', active: true, breakdown: '41 normal, 3 pending' },
  { batchId: 'BATCH-7004', completionDate: '2026-02-22', active: false, breakdown: '22 normal, 7 pending, 1 flagged' },
  { batchId: 'BATCH-7005', completionDate: '2026-02-21', active: true, breakdown: '38 normal, 5 pending' },
  { batchId: 'BATCH-7006', completionDate: '2026-02-20', active: true, breakdown: '31 normal, 4 pending' },
  { batchId: 'BATCH-7007', completionDate: '2026-02-19', active: false, breakdown: '19 normal, 9 pending, 3 flagged' },
  { batchId: 'BATCH-7008', completionDate: '2026-02-18', active: true, breakdown: '44 normal, 2 pending' },
  { batchId: 'BATCH-7009', completionDate: '2026-02-17', active: false, breakdown: '27 normal, 8 pending' },
  { batchId: 'BATCH-7010', completionDate: '2026-02-16', active: true, breakdown: '33 normal, 3 pending, 1 flagged' },
  { batchId: 'BATCH-7011', completionDate: '2026-02-15', active: true, breakdown: '30 normal, 5 pending' },
  { batchId: 'BATCH-7012', completionDate: '2026-02-14', active: false, breakdown: '25 normal, 6 pending, 2 flagged' },
];

export default function LabDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchText, setSearchText] = React.useState('');
  const [providerFilter, setProviderFilter] = React.useState('all');
  const [statusFilter, setStatusFilter] = React.useState('all');
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(8);
  const [resultSearchText, setResultSearchText] = React.useState('');
  const [resultActiveFilter, setResultActiveFilter] = React.useState('all');
  const [resultPage, setResultPage] = React.useState(0);
  const [resultRowsPerPage, setResultRowsPerPage] = React.useState(8);
  const [importBatchOpen, setImportBatchOpen] = React.useState(false);
  const hashKey = location.hash.replace('#', '');
  const activeSection = navItems.some((item) => item.key === hashKey) ? hashKey : 'dashboard';
  const providers = Array.from(new Set(mockOrders.map((order) => order.provider)));
  const statuses = Array.from(new Set(mockOrders.map((order) => order.status)));

  React.useEffect(() => {
    if (!hashKey || !navItems.some((item) => item.key === hashKey)) {
      navigate(`${location.pathname}#dashboard`, { replace: true });
    }
  }, [hashKey, navigate, location.pathname]);

  React.useEffect(() => {
    setPage(0);
  }, [searchText, providerFilter, statusFilter]);

  React.useEffect(() => {
    setResultPage(0);
  }, [resultSearchText, resultActiveFilter]);

  const filteredOrders = mockOrders.filter((order) => {
    const matchesSearch =
      searchText.trim() === '' ||
      `${order.orderId} ${order.patient}`.toLowerCase().includes(searchText.trim().toLowerCase());
    const matchesProvider = providerFilter === 'all' || order.provider === providerFilter;
    const matchesStatus = statusFilter === 'all' || order.status === statusFilter;
    return matchesSearch && matchesProvider && matchesStatus;
  });
  const pagedOrders = filteredOrders.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const filteredResultBatches = mockResultBatches.filter((batch) => {
    const matchesSearch =
      resultSearchText.trim() === '' ||
      `${batch.batchId} ${batch.breakdown}`.toLowerCase().includes(resultSearchText.trim().toLowerCase());
    const matchesActive =
      resultActiveFilter === 'all' ||
      (resultActiveFilter === 'active' && batch.active) ||
      (resultActiveFilter === 'inactive' && !batch.active);
    return matchesSearch && matchesActive;
  });
  const pagedResultBatches = filteredResultBatches.slice(
    resultPage * resultRowsPerPage,
    resultPage * resultRowsPerPage + resultRowsPerPage
  );

  return (
    <Container disableGutters sx={{ mt: 2 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2.5} sx={{ maxWidth: 1120, mx: 'auto' }}>
        <Card
          sx={{
            width: { xs: '100%', md: 260 },
            border: '1px solid #d8e2e7',
            boxShadow: 'none',
            borderRadius: 2,
            alignSelf: 'flex-start',
          }}
        >
          <CardContent sx={{ p: 1 }}>
            <Stack spacing={0.5}>
              {navItems.map((item) => (
                <Button
                  key={item.key}
                  onClick={() => navigate(`${location.pathname}#${item.key}`)}
                  variant={activeSection === item.key ? 'contained' : 'text'}
                  sx={{ justifyContent: 'flex-start', textTransform: 'none', fontWeight: 600 }}
                >
                  {item.label}
                </Button>
              ))}
            </Stack>
          </CardContent>
        </Card>

        <Box sx={{ flex: 1 }}>
          {activeSection === 'dashboard' && (
            <Card
              sx={{
                border: '1px solid #d8e2e7',
                boxShadow: 'none',
                borderRadius: 3,
                background:
                  'linear-gradient(160deg, rgba(0,118,127,0.08) 0%, rgba(255,255,255,1) 55%)',
              }}
            >
              <CardContent sx={{ p: { xs: 3, md: 5 } }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <Typography variant="h3" component="h1">
                    Welcome to Lab Dashboard
                  </Typography>
                  <Typography variant="body1" sx={{ maxWidth: 640 }}>
                    Start a lab workflow by opening intake for a new kit submission.
                  </Typography>
                  <Box sx={{ mt: 2 }}>
                    <Button
                      variant="contained"
                      color="primary"
                      component={RouterLink}
                      to="/mock/lab-kit-intake"
                    >
                      Kit Intake
                    </Button>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          )}

          {activeSection === 'orders' && (
            <Card sx={{ border: '1px solid #d8e2e7', boxShadow: 'none', borderRadius: 3 }}>
              <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
                <Typography variant="h5" component="h2" sx={{ mb: 2 }}>
                  Orders
                </Typography>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ mb: 2 }}>
                  <TextField
                    label="Search Order / Patient"
                    value={searchText}
                    onChange={(event) => setSearchText(event.target.value)}
                    size="small"
                    fullWidth
                  />
                  <TextField
                    select
                    label="Provider"
                    value={providerFilter}
                    onChange={(event) => setProviderFilter(event.target.value)}
                    size="small"
                    sx={{ minWidth: 200 }}
                  >
                    <MenuItem value="all">All Providers</MenuItem>
                    {providers.map((provider) => (
                      <MenuItem key={provider} value={provider}>
                        {provider}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    select
                    label="Status"
                    value={statusFilter}
                    onChange={(event) => setStatusFilter(event.target.value)}
                    size="small"
                    sx={{ minWidth: 180 }}
                  >
                    <MenuItem value="all">All Statuses</MenuItem>
                    {statuses.map((status) => (
                      <MenuItem key={status} value={status}>
                        {status}
                      </MenuItem>
                    ))}
                  </TextField>
                </Stack>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Order ID</TableCell>
                      <TableCell>Patient</TableCell>
                      <TableCell>Provider</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pagedOrders.map((order) => (
                      <TableRow
                        key={order.orderId}
                        hover
                        onClick={() => navigate(`/mock/lab-order/${order.orderId}`)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell>{order.orderId}</TableCell>
                        <TableCell>{order.patient}</TableCell>
                        <TableCell>{order.provider}</TableCell>
                        <TableCell>{order.status}</TableCell>
                      </TableRow>
                    ))}
                    {pagedOrders.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={4}>
                          <Typography variant="body2" color="text.secondary">
                            No orders found for current filters.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
                <TablePagination
                  component="div"
                  count={filteredOrders.length}
                  page={page}
                  onPageChange={(_, newPage) => setPage(newPage)}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={(event) => {
                    setRowsPerPage(parseInt(event.target.value, 10));
                    setPage(0);
                  }}
                  rowsPerPageOptions={[5, 8, 10, 20]}
                />
              </CardContent>
            </Card>
          )}

          {activeSection === 'result-batchs' && (
            <Card sx={{ border: '1px solid #d8e2e7', boxShadow: 'none', borderRadius: 3 }}>
              <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  justifyContent="space-between"
                  alignItems={{ xs: 'stretch', md: 'center' }}
                  spacing={1.5}
                  sx={{ mb: 2 }}
                >
                  <Typography variant="h5" component="h2">
                    Result Batchs
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={() => setImportBatchOpen(true)}
                    sx={{ alignSelf: { xs: 'stretch', md: 'auto' } }}
                  >
                    Import Batch
                  </Button>
                </Stack>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ mb: 2 }}>
                  <TextField
                    label="Search Batch / Breakdown"
                    value={resultSearchText}
                    onChange={(event) => setResultSearchText(event.target.value)}
                    size="small"
                    fullWidth
                  />
                  <TextField
                    select
                    label="Active"
                    value={resultActiveFilter}
                    onChange={(event) => setResultActiveFilter(event.target.value)}
                    size="small"
                    sx={{ minWidth: 180 }}
                  >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                  </TextField>
                </Stack>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Batch ID</TableCell>
                      <TableCell>Completion Date</TableCell>
                      <TableCell>Active</TableCell>
                      <TableCell>Breakdown</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pagedResultBatches.map((batch) => (
                      <TableRow
                        key={batch.batchId}
                        hover
                        onClick={() => navigate(`/mock/lab-results-batch/${batch.batchId}`)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell>{batch.batchId}</TableCell>
                        <TableCell>{batch.completionDate}</TableCell>
                        <TableCell>{batch.active ? 'Yes' : 'No'}</TableCell>
                        <TableCell>{batch.breakdown}</TableCell>
                      </TableRow>
                    ))}
                    {pagedResultBatches.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={4}>
                          <Typography variant="body2" color="text.secondary">
                            No result batches found for current filters.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
                <TablePagination
                  component="div"
                  count={filteredResultBatches.length}
                  page={resultPage}
                  onPageChange={(_, newPage) => setResultPage(newPage)}
                  rowsPerPage={resultRowsPerPage}
                  onRowsPerPageChange={(event) => {
                    setResultRowsPerPage(parseInt(event.target.value, 10));
                    setResultPage(0);
                  }}
                  rowsPerPageOptions={[5, 8, 10, 20]}
                />
              </CardContent>
            </Card>
          )}
        </Box>
      </Stack>

      <ImportBatchDialog open={importBatchOpen} onClose={() => setImportBatchOpen(false)} />
    </Container>
  );
}
